<template>
  <!-- FORGE-412 — Quand le panier est vide ou que le total est a 0
       (mode panier sans selection, erreur de configuration, ...), on
       n'affiche ni les methodes de paiement ni l'iframe Palyance : aucun
       paiement n'est techniquement possible avec un montant nul. On
       affiche a la place un placeholder informatif qui invite
       l'utilisateur a faire une selection. Note : on utilise `v-if` (et
       non `v-show`) pour eviter de pre-charger l'iframe Palyance sur un
       total 0 — `palyanceIframeAlwaysOn` n'a pas d'utilite tant
       qu'aucune offre n'est selectionnee. -->
  <div
    v-if="!hasPayableTotal"
    class="payment-form-empty"
    role="status"
    aria-live="polite"
  >
    {{ $t("messages.selectAtLeastOneOffer") }}
  </div>
  <div v-else class="payment-form">
    <ul v-if="!isEligibleToOneClick">
      <li
        v-if="isBankCardAvailable"
        :class="[
          paymentMethodsOptions?.bank_card?.cssClass,
          'bankcard',
          {
            active:
              selectedPaymentMethod === constants.PAYMENT_METHOD_BANK_CARD,
          },
        ]"
      >
        <label for="bank_card" class="payment-method-tab">
          <input
            type="radio"
            id="bank_card"
            :value="'bank_card'"
            v-model="selectedPaymentMethod"
          />
          <!-- SVG inline (au lieu de l'icone Font Awesome `fa-credit-card`
               qui rend un glyph aleatoire selon la version de FA chargee).
               Couleur heritee via currentColor. -->
          <svg
            v-if="isOrderPage"
            class="payment-method-icon"
            viewBox="0 0 24 16"
            width="24"
            height="16"
            aria-hidden="true"
          >
            <rect
              x="0.5"
              y="0.5"
              width="23"
              height="15"
              rx="2"
              fill="none"
              stroke="currentColor"
            />
            <rect x="0.5" y="3.5" width="23" height="3" fill="currentColor" />
            <rect
              x="3"
              y="10"
              width="6"
              height="2"
              rx="0.5"
              fill="currentColor"
            />
          </svg>
          <span class="payment-method-tab__text">{{
            $t("labels.bankCard")
          }}</span>
          <!-- Pills brands : visuel informatif uniquement (rappel des
               reseaux supportes par Palyance). Aucun comportement. -->
          <span
            v-if="isOrderPage"
            class="payment-method-brands"
            aria-hidden="true"
          >
            <span class="payment-method-brand payment-method-brand--visa"
              >VISA</span
            >
            <span class="payment-method-brand payment-method-brand--mc"
              >MC</span
            >
          </span>
        </label>
      </li>
      <li
        v-if="isSepaAvailable"
        :class="[
          paymentMethodsOptions?.sepa?.cssClass,
          'sepa',
          { active: selectedPaymentMethod === constants.PAYMENT_METHOD_SEPA },
        ]"
      >
        <label for="sepa" class="payment-method-tab">
          <input
            type="radio"
            id="sepa"
            :value="'sepa'"
            v-model="selectedPaymentMethod"
          />
          <!-- SVG inline (au lieu de l'icone FA `fa-building-columns`).
               Batiment grec stylise : toit triangulaire + colonnes + base.
               Couleur heritee via currentColor. -->
          <svg
            v-if="isOrderPage"
            class="payment-method-icon"
            viewBox="0 0 24 16"
            width="24"
            height="16"
            aria-hidden="true"
          >
            <polygon points="12,1 22,7 2,7" fill="currentColor" />
            <rect x="4" y="7" width="2" height="6" fill="currentColor" />
            <rect x="11" y="7" width="2" height="6" fill="currentColor" />
            <rect x="18" y="7" width="2" height="6" fill="currentColor" />
            <rect x="2" y="13" width="20" height="2" fill="currentColor" />
          </svg>
          <span class="payment-method-tab__text">{{ $t("labels.sepa") }}</span>
          <span
            v-if="isOrderPage"
            class="payment-method-brands"
            aria-hidden="true"
          >
            <span class="payment-method-brand payment-method-brand--sepa"
              >SEPA</span
            >
          </span>
        </label>
      </li>
    </ul>

    <div
      v-if="!isEligibleToOneClick && hasPaymentError"
      class="alert alert-warning"
    >
      {{ $t("messages.paymentError") }}
    </div>
    <OneClick
      v-if="isEligibleToOneClick && !isVanguardOnlyPreviewMode"
      class="btn-1c"
    />
    <button
      v-else-if="isVanguardOnlyPreviewMode && isFormValid"
      type="button"
      class="btn btn-primary process-order-cta add-to-cart-btn vanguard-only-preview-pay"
      :disabled="loading"
      @click.prevent="validatePreviewOrder"
    >
      {{ $t("labels.previewTestPayment") }}
    </button>
    <iframe
      v-else-if="
        !isVanguardOnlyPreviewMode &&
        (isFormValid || palyanceIframeAlwaysOn) &&
        iframeUrl
      "
      ref="paymentIFrame"
      v-show="isOrderPage ? true : !loading"
      :src="iframeUrl"
      allowtransparency="true"
      class="vanguard-payment-iframe"
      :class="{ 'is-reloading': isOrderPage && iframeReloading }"
      :style="iframeStyle"
      @load="initPaymentIFrame"
      @error="onIframeError"
    ></iframe>
    <div
      v-if="isFormValid && loading && !isEligibleToOneClick && !isOrderPage"
      class="loading"
    ></div>
  </div>
</template>

<script>
import { useDraftStore } from "@/store/draft.js";
import { useCartStore } from "@/store/cart.js";
import { useAspspStore } from "@/store/aspsp";
import { useTrackingStore } from "@/store/tracking";
import { useOrderHistoryStore } from "@/store/orderHistory";
import { constants } from "@/constants";
import OneClick from "@/components/checkout/OneClick.vue";
import Palyance from "@/services/palyance";
import OneClickService from "@/services/payment/oneClick";
import Logger from "@/services/logger";
import Navigation from "@/services/navigation";
import {
  buildOrderPagePaymentKoUrl,
  getPaymentRetryOrderId,
} from "@/services/orderPageRetryParam";
import Utils from "@/services/utils";
import CartService from "@/services/cart";
import DuplicateOrderGuard from "@/services/duplicateOrderGuard";
import PriceDisplay from "@/services/priceDisplay";
import {
  getOrderPageContextVersion,
  isOrderPageContext,
} from "@/services/orderPageScope";
import {
  isVanguardOnlyPreview,
  VANGUARD_ONLY_TEST_LEVEL,
} from "@/services/testFixtures";
import { clearPreviewExtrasellConsumed } from "@/services/extrasellPayload";
import { markVanguardOnlyPreviewTab } from "@/services/confirmationPreview";
import Redirect from "@/services/redirect";

export default {
  name: "CheckoutPaymentForm",
  components: { OneClick },
  props: {
    paymentMethodsOptions: {
      type: Object,
      required: false,
      default: {},
    },
    orderType: {
      type: String,
      required: false,
      default: null, // 'upsell', 'crosssell', or null for initial orders
    },
  },
  data() {
    return {
      constants,
      cart: null,
      isFormValid: false,
      selectedPaymentMethod: null,
      iframeHeightPalyance: 450,
      iframeUrl: null,
      paymentFormLoading: false,
      hasPaymentError: false,
      isBankCardAvailable: true,
      isSepaAvailable: true,
      palyanceIframeAlwaysOn: false,
      retryOrderId: null,
      retryItem: null,
      // Order Page UX: keep iframe height stable across reloads
      lastIframeHeight: 450,
      // Order Page UX: cross-fade opacity during reload (200ms)
      iframeReloading: false,
      // Debounce handle for selectedPaymentMethod -> updateIframeUrl (Order Page only)
      iframeUrlDebounceTimer: null,
      // Safety net: force-clear iframeReloading if @load never fires (network error, blocked content).
      iframeReloadSafetyTimer: null,
    };
  },
  computed: {
    loading() {
      return useDraftStore().isSendingDraftOrder || this.paymentFormLoading;
    },
    draftIsValid() {
      return useDraftStore().isFormValid;
    },
    lastCartUpdate() {
      return useCartStore().lastUpdate;
    },
    order() {
      return useDraftStore().order;
    },
    isEligibleToOneClick() {
      return OneClickService.isEligibleToOneClick();
    },
    isOrderPage() {
      getOrderPageContextVersion().value;
      return isOrderPageContext();
    },
    isVanguardOnlyPreviewMode() {
      return isVanguardOnlyPreview();
    },
    // FORGE-412 — Vrai quand il existe au moins une offre dans le cart
    // ET que le total est strictement positif. Conditionne l'affichage
    // de la `<div class="payment-form">` (methodes de paiement +
    // iframe). Quand faux (cart vide en mode multi-offres avant
    // selection, ou cart non vide mais total a 0 suite a un coupon /
    // erreur de configuration), on rend a la place un placeholder
    // informatif (`.payment-form-empty`).
    //
    // On lit `useCartStore().cart` plutot que `this.cart` (data local) :
    // le data local est rafraichi par le watcher `lastCartUpdate` au
    // tick suivant, alors que le computed doit refleter immediatement
    // l'etat du store apres `emptyCart + addToCart`. On declare la
    // dependance reactive sur `lastCartUpdate` (incremente par chaque
    // mutation atomique du cart) pour forcer la recompute Vue.
    //
    // Un abonnement avec periode d'intro offerte (ex: "1er mois offert"
    // puis 49 EUR / trimestre) a un total immediat a 0 mais un montant
    // recurrent a venir : l'iframe Palyance doit quand meme se charger
    // pour enregistrer le moyen de paiement. On considere donc payable
    // un cart dont un item a une offre d'intro suivie d'une ligne de
    // renouvellement, meme si le total courant est nul.
    hasPayableTotal() {
      void this.lastCartUpdate;
      const cart = useCartStore().cart;
      if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
        return false;
      }
      if (typeof cart.total === "number" && cart.total > 0) {
        return true;
      }
      return cart.items.some(
        (item) =>
          PriceDisplay.shouldShowIntroOffer(item?.choice) &&
          PriceDisplay.hasAfterLine(item?.choice),
      );
    },
    iframeStyle() {
      // On Order Pages: keep last known height during reload to avoid layout shift.
      const height =
        this.isOrderPage && this.iframeReloading
          ? this.lastIframeHeight
          : this.iframeHeightPalyance;
      return `height: ${height}px`;
    },
  },
  watch: {
    lastCartUpdate: {
      handler(newValue, oldValue) {
        if (!window?.globalConfig?.domainId) {
          return;
        }
        this.cart = this.getCart();

        // FORGE-412 — Synchronisation iframe Palyance sur changement de
        // cart en order page.
        //
        // Sur une order page (limitCartTo1Item=true), basculer d'une
        // offre a une autre fait `emptyCart + addToCart`. Le cart
        // change (total, items[*].choice.freeDays, ...) mais aucun
        // autre watcher ne propage ce changement vers Palyance :
        //   - `isFormValid` reste `true` (cart non vide + draft valid)
        //     -> watcher `isFormValid` ne se redeclenche pas.
        //   - `order` (Pinia draft) n'est pas mute -> watcher `order`
        //     ne se redeclenche pas.
        //   - `selectedPaymentMethod` est inchange.
        //
        // On declenche la sync ici (et pas dans le watcher `cart` deep)
        // car `lastCartUpdate` est l'evenement canonique d'une
        // mutation atomique du cart : il s'incremente une seule fois
        // par cycle complet `emptyCart + addToCart` (apres le dernier
        // `saveCart`), et le handler a ce moment-la voit
        // useCartStore().cart deja completement recalcule (total,
        // subtotal, etc.). Mettre la sync dans `cart` deep avait un
        // probleme de fenetre : `this.cart` (data local) etait
        // reaffectee mais Vue pouvait, dans certains enchainements,
        // executer le handler avec encore l'ancien snapshot
        // (symptome rapporte : iframe d'un cran en retard).
        //
        // On lit explicitement `this.checkFormValidity()` plutot que
        // `this.isFormValid` : ce dernier est un data field mis a
        // jour par le watcher `cart` deep, qui n'a pas encore fire
        // a cet instant.
        if (
          this.isOrderPage &&
          this.checkFormValidity() &&
          !this.isVanguardOnlyPreviewMode
        ) {
          const isUpdatedDynamically = this.updatePalyanceDynamically();
          if (!isUpdatedDynamically) {
            this.scheduleIframeUrlUpdate();
          }
        }
      },
    },
    cart: {
      handler(newValue, oldValue) {
        if (!window?.globalConfig?.domainId) {
          return;
        }
        // In retry mode, keep form valid
        if (!this.retryOrderId) {
          this.isFormValid = this.checkFormValidity();
        }
      },
      deep: true,
    },
    draftIsValid: {
      handler(newValue, oldValue) {
        if (!window?.globalConfig?.domainId) {
          return;
        }
        // In retry mode, keep form valid
        if (!this.retryOrderId) {
          this.isFormValid = this.checkFormValidity();
        }
      },
    },
    selectedPaymentMethod: function (newVal, oldVal) {
      useDraftStore().selectPaymentMethod(newVal);
      this.setAspspData(newVal);
      this.scheduleIframeUrlUpdate();
    },
    isFormValid: function (newVal, oldVal) {
      if (newVal && !this.isVanguardOnlyPreviewMode) {
        const isUpdatedDynamically = this.updatePalyanceDynamically();
        if (!isUpdatedDynamically) {
          this.scheduleIframeUrlUpdate();
        }
      }
    },
    order: {
      handler(newVal, oldVal) {
        this.updateShippingAndBillingInformations();
        if (this.isVanguardOnlyPreviewMode) {
          return;
        }
        const isUpdatedDynamically = this.updatePalyanceDynamically();
        if (!isUpdatedDynamically) {
          this.scheduleIframeUrlUpdate();
        }
      },
      deep: true,
    },
    // FORGE-412 — Au passage `false -> true` (premiere offre ajoutee a
    // un cart vide), l'iframe Palyance vient juste d'etre creee par le
    // v-if du template. On declenche un `updateIframeUrl()` pour que
    // son `:src` reflete le total a jour. Sans ce watcher, l'iframe
    // restait sur l'URL initiale (amount=0) puisque `updatePalyanceDynamically`
    // ne peut pas postMessage tant que l'iframe n'est pas dans le DOM.
    hasPayableTotal(newValue, oldValue) {
      if (
        newValue &&
        !oldValue &&
        this.palyanceIframeAlwaysOn &&
        !this.isVanguardOnlyPreviewMode
      ) {
        this.scheduleIframeUrlUpdate();
      }
    },
  },
  mounted() {
    this.retryOrderId = getPaymentRetryOrderId(
      new URL(window.location).searchParams,
    );

    if (this.retryOrderId) {
      this.handleRetryMode();
    }

    // TODO : utiliser la method PaymentMethodService.getDefaultPaymentMethod()

    this.cart = this.getCart();
    // In retry mode, form is already valid - don't override with checkFormValidity
    if (!this.retryOrderId) {
      this.isFormValid = this.checkFormValidity();
    } else {
      this.isFormValid = true;
    }
    this.setPaymentMethodAvailability();

    if (!this.isSepaAvailable && !this.isBankCardAvailable) {
      console.error("No payment method available");
      return;
    }

    if (useDraftStore().selectedPaymentMethod) {
      this.selectedPaymentMethod = useDraftStore().selectedPaymentMethod;
    } else if (this.isVanguardOnlyPreviewMode && this.isSepaAvailable) {
      this.selectedPaymentMethod = constants.PAYMENT_METHOD_SEPA;
    } else {
      if (this.isBankCardAvailable) {
        this.selectedPaymentMethod = constants.PAYMENT_METHOD_BANK_CARD;
      } else if (this.isSepaAvailable) {
        this.selectedPaymentMethod = constants.PAYMENT_METHOD_SEPA;
      }
    }

    this.getAspspEligibilityData();

    this.palyanceIframeAlwaysOn =
      window.globalConfig.palyanceIframeAlwaysOn || false;
    // FORGE-412 — Ne pas pre-charger l'iframe avec une URL contenant
    // `amount=0` quand le cart est vide (placeholder `.payment-form-empty`
    // affiche). L'URL sera construite au moment ou une offre est ajoutee,
    // via le watcher `hasPayableTotal` ci-dessous (ou via le watcher
    // `lastCartUpdate` qui declenche `scheduleIframeUrlUpdate()` une fois
    // `updatePalyanceDynamically()` ayant signale l'absence d'iframe).
    if (
      this.palyanceIframeAlwaysOn &&
      this.hasPayableTotal &&
      !this.isVanguardOnlyPreviewMode
    ) {
      this.updateIframeUrl();
    }
  },
  beforeUnmount() {
    if (this.iframeUrlDebounceTimer) {
      clearTimeout(this.iframeUrlDebounceTimer);
      this.iframeUrlDebounceTimer = null;
    }
    if (this.iframeReloadSafetyTimer) {
      clearTimeout(this.iframeReloadSafetyTimer);
      this.iframeReloadSafetyTimer = null;
    }
  },
  methods: {
    getCart() {
      return useCartStore().cart;
    },
    checkFormValidity() {
      return this.getCart() && this.getCart().items.length && this.draftIsValid;
    },
    setPaymentMethodAvailability() {
      // if config not exists : display both payment methods or config exists and bank_card is available
      this.isBankCardAvailable =
        !window.globalConfig.paymentMethodsAvailable ||
        !window.globalConfig.paymentMethodsAvailable.includes(",") ||
        window.globalConfig.paymentMethodsAvailable
          .split(",")
          .includes("bank_card");

      // if config not exists : display both payment methods or config exists and bank_card is available
      this.isSepaAvailable =
        !window.globalConfig.paymentMethodsAvailable ||
        !window.globalConfig.paymentMethodsAvailable.includes(",") ||
        window.globalConfig.paymentMethodsAvailable.split(",").includes("sepa");
    },
    scheduleIframeUrlUpdate() {
      if (this.isVanguardOnlyPreviewMode) {
        this.paymentFormLoading = false;
        return;
      }

      // Order Page UX: debounce rapid offer toggles to avoid stacked reloads.
      // Debounce delay tuned to Palyance reload time (~250ms).
      const debounceMs = 250;

      if (!this.isOrderPage) {
        this.updateIframeUrl();
        return;
      }

      if (this.iframeUrlDebounceTimer) {
        clearTimeout(this.iframeUrlDebounceTimer);
      }

      this.iframeUrlDebounceTimer = setTimeout(() => {
        this.iframeUrlDebounceTimer = null;
        this.updateIframeUrl();
      }, debounceMs);
    },
    updateIframeUrl() {
      if (this.isVanguardOnlyPreviewMode) {
        this.paymentFormLoading = false;
        return null;
      }

      if (
        !window.globalConfig ||
        (!this.isFormValid && !this.palyanceIframeAlwaysOn)
      ) {
        return null;
      }

      let newIframeUrl;

      if (this.selectedPaymentMethod === constants.PAYMENT_METHOD_SEPA) {
        newIframeUrl = `${process.env.VUE_APP_PALYANCE_APP_FRONT_URL}/sepa/capture/?${this.getPalyanceV2QueryParams()}`;
      } else {
        newIframeUrl = `${process.env.VUE_APP_PALYANCE_APP_FRONT_URL}/bankcard/capture/?${this.getPalyanceV2QueryParams()}`;
      }

      if (newIframeUrl !== this.iframeUrl) {
        this.paymentFormLoading = true;
        // Order Page UX: keep last known height + start fade-out before swap.
        if (this.isOrderPage) {
          this.lastIframeHeight = this.iframeHeightPalyance;
          this.iframeReloading = true;
          this.armIframeReloadSafetyNet();
        }
        this.iframeUrl = newIframeUrl;
      } else {
        console.log("Iframe URL did not change");
      }
    },
    armIframeReloadSafetyNet() {
      // Clear any previous timer (rapid debounced reloads).
      if (this.iframeReloadSafetyTimer) {
        clearTimeout(this.iframeReloadSafetyTimer);
      }
      // 5s = generous upper bound on Palyance load time. Beyond that, fade back in
      // to avoid a permanently invisible iframe if @load never fires.
      this.iframeReloadSafetyTimer = setTimeout(() => {
        this.iframeReloadSafetyTimer = null;
        if (this.iframeReloading) {
          console.warn(
            "[vanguard-v2] iframe @load did not fire within 5s, clearing reloading state",
          );
          this.iframeReloading = false;
          this.paymentFormLoading = false;
        }
      }, 5000);
    },
    onIframeError(event) {
      console.error("[vanguard-v2] iframe failed to load", event);
      // Avoid a permanently invisible iframe (opacity: 0) on network error.
      this.iframeReloading = false;
      this.paymentFormLoading = false;
      if (this.iframeReloadSafetyTimer) {
        clearTimeout(this.iframeReloadSafetyTimer);
        this.iframeReloadSafetyTimer = null;
      }
    },
    updatePalyanceDynamically() {
      if (!this.palyanceIframeAlwaysOn) {
        return false;
      }

      // FORGE-412 — Quand l'iframe n'a jamais ete montee dans le DOM
      // (cas typique : on vient de passer de `hasPayableTotal=false` a
      // `true` apres ajout d'une offre dans un cart vide), `$refs.paymentIFrame`
      // est null. On ne peut pas postMessage : on signale au caller qu'il
      // doit refaire un `updateIframeUrl()` complet (la nouvelle iframe
      // sera creee avec une URL portant le bon montant). Sans ce signal,
      // l'iframe se montait avec l'URL initiale (amount=0) et le montant
      // restait fige a 0 dans le tunnel Palyance.
      if (!this.$refs.paymentIFrame) {
        return false;
      }

      const paymentIFrame = this.$refs.paymentIFrame.contentWindow;
      paymentIFrame.postMessage(
        { json: JSON.stringify(this.buildPalyanceV2ParamsObject()) },
        process.env.VUE_APP_PALYANCE_APP_FRONT_URL,
      );

      return true;
    },
    getAspspEligibilityData() {
      let sfId = null,
        walletIdentifier = null;
      const regexIdentifier = /^[a-zA-Z0-9]*$/;

      if (useTrackingStore().urlParams.sfId) {
        sfId = useTrackingStore().urlParams.sfId;

        if (sfId && !regexIdentifier.test(sfId)) {
          sfId = sfId.replace(/[^a-z0-9.]+/gi, "");
        }
      }

      if (
        useTrackingStore().urlParams.walletId &&
        regexIdentifier.test(useTrackingStore().urlParams.walletId)
      ) {
        useDraftStore().aspspEligibilityLoading(true);
        walletIdentifier = useTrackingStore().urlParams.walletId;

        Palyance.getAspspEligibility(
          walletIdentifier,
          this.selectedPaymentMethod,
          window.globalConfig.affiliateId,
        )
          .then((data) => {
            useDraftStore().aspspEligibilityLoading(false);

            if (JSON.stringify(data) === "{}") {
              useDraftStore().eligibleToOneClick(false);
              useAspspStore().eligible(false);
              return;
            }

            const paymentData = data[this.selectedPaymentMethod];
            const shippingCountryPreffiled =
              paymentData.content.SHIPPINGCOUNTRY__C ||
              paymentData.content.BILLINGCOUNTRY__C;

            // Check if the country is excluded
            if (
              window.globalConfig.countriesExcluded &&
              window.globalConfig.countriesExcluded.includes(
                shippingCountryPreffiled,
              )
            ) {
              console.log(
                `[vanguard] Country ${shippingCountryPreffiled} are excluded so the prefilled data will not be used`,
              );
              useAspspStore().eligible(false);
              return;
            }
            useAspspStore().eligible(true);

            /*  If SEPA is not aspsp eligible, but BANK_CARD is eligible and available => BANK_CARD
                            If any other situation => SEPA :
                                - aspsp SEPA eligible OR
                                - not aspsp BANK_CARD eligible OR
                                - no payment method eligible OR
                                - BANK_CARD not available
                         */
            if (
              !data.sepa.paymentIdentifier &&
              data.bank_card.paymentIdentifier &&
              this.isBankCardAvailable
            ) {
              this.selectedPaymentMethod = constants.PAYMENT_METHOD_BANK_CARD;
            } else {
              this.selectedPaymentMethod = constants.PAYMENT_METHOD_SEPA;
            }

            if (data.bank_card.paymentIdentifier) {
              useAspspStore().updateAspspEligibleBankCard(true);
            }
            if (data.sepa.paymentIdentifier) {
              useAspspStore().updateAspspEligibleSepa(true);
            }

            useAspspStore().updateAspspPaymentInformations(data);

            this.setAspspData(this.selectedPaymentMethod);
            this.setAspspContent(this.selectedPaymentMethod);

            if (!this.isVanguardOnlyPreviewMode) {
              this.updateIframeUrl();
            }
          })
          .catch((error) => {
            console.error(error);
            useDraftStore().aspspEligibilityLoading(false);
            useDraftStore().eligibleToOneClick(false);
            useAspspStore().eligible(false);
            Logger.error("Error on getAspspEligibility call", error);
          });
      }
    },
    formatFullAddress(data) {
      const billingStreet = data.BILLINGSTREET3__C;
      const billingZipCode = data.BILLINGZIPCODE__C;
      const billingCity = data.BILLINGCITY__C;

      const shippingStreet = data.SHIPPINGSTREET3__C || data.BILLINGSTREET3__C;
      const shippingZipCode = data.SHIPPINGZIPCODE__C || data.BILLINGZIPCODE__C;
      const shippingCity = data.SHIPPINGCITY__C || data.BILLINGCITY__C;
      const billingAddress = `${billingStreet}, ${billingZipCode} ${billingCity}`;
      const shippingAddress = `${shippingStreet}, ${shippingZipCode} ${shippingCity}`;

      if (billingAddress !== shippingAddress) {
        useDraftStore().updateIsDistinctAdress(true);
        return {
          billingAddress,
          shippingAddress,
        };
      } else {
        useDraftStore().updateIsDistinctAdress(false);
        return billingAddress;
      }
    },
    mapLanguageToOgoneFormat(lang) {
      const langMap = {
        fr: "fr_FR",
        en: "en_US",
        es: "es_ES",
        de: "de_DE",
        it: "it_IT",
      };

      return langMap[lang] || "fr_FR";
    },
    resolveOrderPageAccentColor() {
      const fromConfig = window.globalConfig?.accentColor;
      if (
        typeof fromConfig === "string" &&
        /^#[0-9A-Fa-f]{6}$/.test(fromConfig.trim())
      ) {
        return fromConfig.trim().toLowerCase();
      }

      const fromCss = getComputedStyle(document.documentElement)
        .getPropertyValue("--vg-accent")
        .trim();
      if (/^#[0-9A-Fa-f]{6}$/i.test(fromCss)) {
        return fromCss.toLowerCase();
      }

      return null;
    },
    buildPalyanceV2ParamsObject() {
      const exceptionUrl =
        window.globalConfig?.isOrderPage && window.globalConfig?.orderPageSlug
          ? buildOrderPagePaymentKoUrl(
              window.location.origin,
              window.globalConfig.orderPageSlug,
              this.order?.externalOrderId,
            )
          : `${window.location.origin}${window.globalConfig?.errorPage || "/error"}`;
      const waitingUrl = `${window.location.origin}${window.globalConfig.waitingPage}`;
      const waitingDoubleOptinUrl = `${window.location.origin}${window.globalConfig.waitingDoubleOptinPage}`;
      this.updateShippingAndBillingInformations();
      const order = this.order;

      // FORGE-412 — On lit `useCartStore().cart` plutot que `this.cart`
      // (data local). `this.cart` n'est rafraichi qu'apres le tick
      // des watchers Vue ; sur une order page, l'enchainement
      // `emptyCart + addToCart` mute le cart 3 fois en JS synchrone et
      // l'utilisateur a rapporte une iframe d'un cran en retard (le
      // payload contenait le total de l'offre precedente). Lire le
      // store garantit qu'on prend toujours la photo la plus recente
      // au moment ou le payload est serialise pour postMessage / URL.
      const currentCart = useCartStore().cart;
      const shippingInformations = {
        type: "shipping",
        firstName: order.shippingFirstName,
        lastName: order.shippingLastName,
        address: order.shippingStreet1 || "",
        address2: order.shippingStreet2 || "",
        address3: order.shippingStreet3 || "",
        address4: order.shippingStreet4 || "",
        phone: order.shippingPhoneNumber || "",
        zipCode: order.shippingZipCode,
        city: order.shippingCity,
        country: order.shippingCountry,
      };

      const paramPlus = this.buildParamPlus();

      const payload = {
        amount: currentCart?.total ?? 0,
        totalAmount: currentCart?.total ?? 0,
        merchantIdentifier: window.globalConfig.affiliateId,
        merchantName: window.globalConfig.brandName,
        doubleOptin: useAspspStore().isDoubleOptin ? 1 : 0,
        defaultLocale: this.mapLanguageToOgoneFormat(window.globalConfig.lang),
        lang: window.globalConfig.lang,
        orderId: this.order.externalOrderId,
        orderType: this.orderType, // For upsell delta calculation (BUILD-1680)
        initialExternalOrderId: this.order.initialExternalOrderId, // For upsell delta lookup (BUILD-1680)
        billingInformations: {
          firstName: order.billingFirstName,
          lastName: order.billingLastName,
          address: order.billingStreet1 || "",
          address2: order.billingStreet2 || "",
          address3: order.billingStreet3 || "",
          address4: order.billingStreet4 || "",
          phone: order.billingPhoneNumber || "",
          zipCode: order.billingZipCode,
          city: order.billingCity,
          country: order.billingCountry,
          email: order.billingEmail?.toLowerCase(),
        },
        shippingInformations: shippingInformations,
        technicalContract: useDraftStore().areCgvApproved ? 1 : 0,
        aspspContract: useDraftStore().areCgsApproved ? 1 : 0,
        sourceUrl: encodeURIComponent(document.location.href),
        source: "vanguardv2",
        formIsValid:
          useDraftStore().isFormValid && useCartStore().cart?.items?.length > 0,
        productName: "shop",
        browserData: this.presentBrowserData(),
        waitingUrl: waitingUrl,
        exceptionUrl: exceptionUrl,
        palyanceIframeAlwaysOn: this.palyanceIframeAlwaysOn,
      };

      if (
        this.getCart() &&
        this.getCart().items.length === 1 &&
        this.getCart().items[0].choice.freeDays
      ) {
        payload.choiceFreeDays = this.getCart().items[0].choice.freeDays;
      }

      if (
        (this.selectedPaymentMethod === constants.PAYMENT_METHOD_SEPA &&
          useAspspStore().isAspspEligibleSepa) ||
        (this.selectedPaymentMethod === constants.PAYMENT_METHOD_BANK_CARD &&
          useAspspStore().isAspspEligibleBankCard)
      ) {
        const regex = /^[a-zA-Z0-9]*$/;
        const walletId =
          useTrackingStore().urlParams.walletIdentifier ||
          useTrackingStore().urlParams.walletId;
        const queryParams = Navigation.getQueryParams();
        const paymentId =
          queryParams.get("paymentId") || useAspspStore().paymentIdentifier; // paymentId is used when recapture

        payload.walletIdentifier = regex.test(walletId) ? walletId : null;
        payload.paymentInformationIdentifier = paymentId;
        payload.mandateIdentifier = useAspspStore().mandateIdentifier;
        payload.paymentInformationObfuscated =
          useAspspStore().paymentAccountIdentifier;
        payload.pciToken = useAspspStore().pciToken;
        payload.holderName = useAspspStore().holderName;

        if (this.selectedPaymentMethod === constants.PAYMENT_METHOD_SEPA) {
          payload.waitingUrl = waitingDoubleOptinUrl;

          paramPlus.emailDoubleOptin = useAspspStore().email;
          paramPlus.countdownStartTimeMs = Date.now();
        }
      }

      if (this.paymentMethodsOptions.iframeStyle) {
        payload.iframeStyle = "orderPage";
      }

      const accentColor = this.resolveOrderPageAccentColor();
      if (accentColor) {
        payload.accentColor = accentColor;
      }

      // Pass retry flag to Palyance to disable one-click when retrying after a payment error
      if (this.retryOrderId || this.order?.isRetry) {
        payload.retry = true;
      }

      localStorage.setItem(constants.PARAM_PLUS, JSON.stringify(paramPlus));

      return payload;
    },
    getPalyanceV2QueryParams() {
      const payload = this.buildPalyanceV2ParamsObject();

      const queryParams = new URLSearchParams();
      queryParams.append("json", JSON.stringify(payload));

      return queryParams.toString();
    },
    presentBrowserData() {
      if (typeof window.screen === "undefined") {
        window.screen = {};
      }

      if (
        typeof window.navigator === "undefined" ||
        typeof window.navigator.javaEnabled !== "function"
      ) {
        window.navigator = {
          javaEnabled() {
            return false;
          },
        };
      }

      return {
        colorDepth: window.screen.colorDepth,
        javaEnabled: window.navigator.javaEnabled().toString(),
        language: window.navigator.language,
        screenHeight: window.screen.height,
        screenWidth: window.screen.width,
        timeZone: new Date().getTimezoneOffset(),
      };
    },
    buildParamPlus() {
      const paramPlus = {};

      if (window.location.search.length > 0) {
        window.location.search
          .slice(1)
          .split("&")
          .forEach((pair) => {
            const row = pair.split("=");
            paramPlus[row[0]] = row[1];
          });
      }

      paramPlus.orderId = this.order.externalOrderId;
      //paramPlus.confirmationUrl = confirmationUrl;
      //paramPlus.exceptionUrl = exceptionUrl;
      paramPlus.paymentType = this.selectedPaymentMethod;
      paramPlus.action = "capture";
      paramPlus.retryUrl = `${window.location.origin}${window.location.pathname.replace(/\/$/, "")}?retry=${this.order.externalOrderId}`;

      return paramPlus;
    },
    /**
     * Front-only checkout for ?testLevel=vanguard_only (vanguard-front parity).
     * Skips Palyance / Orion and jumps to confirmation with a seeded history.
     */
    validatePreviewOrder() {
      if (!this.isVanguardOnlyPreviewMode) {
        return;
      }

      const paymentType =
        this.selectedPaymentMethod || constants.PAYMENT_METHOD_SEPA;
      useDraftStore().selectPaymentMethod(paymentType);

      const preparedOrder = {
        ...useDraftStore().order,
        status: "completed",
        isPaymentCaptured: true,
        paymentType,
        orderType: this.orderType || "initial",
        testLevel: VANGUARD_ONLY_TEST_LEVEL,
        url: window.location.href,
      };

      if (!preparedOrder.externalOrderId) {
        preparedOrder.externalOrderId = Utils.generateUID();
      }

      useDraftStore().updateOrder(preparedOrder);
      useOrderHistoryStore().addOrderToHistory(
        useCartStore().cart,
        preparedOrder,
        0,
      );
      clearPreviewExtrasellConsumed(window?.globalConfig?.domainId);
      markVanguardOnlyPreviewTab();

      const paramPlus = this.buildParamPlus();
      paramPlus.testLevel = VANGUARD_ONLY_TEST_LEVEL;
      paramPlus.orderId = preparedOrder.externalOrderId;
      paramPlus.paymentType = paymentType;
      localStorage.setItem(constants.PARAM_PLUS, JSON.stringify(paramPlus));

      Redirect.redirectToConfirmation(paramPlus);
    },
    handleRetryMode() {
      this.retryItem = useOrderHistoryStore().currentPageOrderHistory.find(
        (item) => item.order.externalOrderId === this.retryOrderId,
      );

      if (this.retryItem) {
        // Rebuild cart from previous order
        this.rebuildCartFromRetry();

        // Create new retry order with new ID
        const newOrder = {
          ...this.retryItem.order,
          externalOrderId: Utils.generateUID(),
          status: "pending",
          isRetry: true,
        };
        useDraftStore().updateOrder(newOrder);

        // Select same payment method as before
        this.selectedPaymentMethod = this.retryItem.order.paymentType;

        // Show error message
        this.hasPaymentError = true;

        // Mark form as valid since we're retrying
        useDraftStore().formValid(true);
      }
    },
    rebuildCartFromRetry() {
      // Clear existing cart first
      CartService.emptyCart();

      // Reconstruct cart from previous order history
      for (const oldCartItem of this.retryItem.cart.items) {
        const newCartItem = { ...oldCartItem };
        newCartItem.qtyIncr = 1;

        // Ensure choice.name exists (may be missing in older orderHistory entries)
        if (newCartItem.choice && !newCartItem.choice.name) {
          newCartItem.choice.name = newCartItem.name || "Product";
        }

        CartService.addToCart(newCartItem);
      }
    },
    initPaymentIFrame() {
      // Listen to IFrame messages to retrieve payment data
      window.addEventListener("message", this.iframeMessageCallback, false);

      if (!this.$refs.paymentIFrame) {
        console.error("Unable to retrieve payment IFrame DOM node");
      }

      const paymentIFrame = this.$refs.paymentIFrame.contentWindow;
      paymentIFrame.postMessage("FrameHeightPalyance", "*");

      this.paymentFormLoading = false;

      // Order Page UX: fade-in once iframe finished loading.
      if (this.isOrderPage && this.iframeReloading) {
        if (this.iframeReloadSafetyTimer) {
          clearTimeout(this.iframeReloadSafetyTimer);
          this.iframeReloadSafetyTimer = null;
        }
        // Defer one tick so the CSS transition kicks in.
        this.$nextTick(() => {
          this.iframeReloading = false;
        });
      }
    },
    async iframeMessageCallback(event) {
      // Check whether message origin is Palyance origin
      if (event.origin !== process.env.VUE_APP_PALYANCE_APP_FRONT_URL) {
        console.debug("Origin does not match with Palyance origin", event);
        return;
      }

      if (
        event &&
        event.data &&
        typeof event.data.FrameHeightPalyance !== "undefined"
      ) {
        console.debug(
          "event.data.FrameHeightPalyance",
          event.data.FrameHeightPalyance,
        );
        this.iframeHeightPalyance = event.data.FrameHeightPalyance;
        return;
      }

      if (
        event &&
        event.data &&
        typeof event.data.contactFormCheckValidation !== "undefined"
      ) {
        // handled in contactForm component
        return;
      }

      if (
        event &&
        event.data &&
        typeof event.data.redirectUrl !== "undefined"
      ) {
        const blocked = await DuplicateOrderGuard.blockIfDuplicate(
          useCartStore().cart,
          this.order,
          { orderType: this.orderType },
        );
        if (blocked) {
          return;
        }

        // Save order to history before redirecting (for retry functionality)
        if (this.orderType === "initialOrder" || !this.orderType) {
          useOrderHistoryStore().addOrderToHistory(
            useCartStore().cart,
            this.order,
            0,
          );
        }
        window.location.href = event.data.redirectUrl;
        return;
      }

      const response = event.data;
      if (typeof response.isLoaded !== "undefined") {
        return;
      }

      // If an error occurred, display error message then reset IFrame
      if (!response.status) {
        console.error("Palyance Payment error", response.data);
        this.hasPaymentError = true;
        return;
      }
    },
    setAspspData(paymentMethod) {
      useAspspStore().updateAspspData(paymentMethod);
    },
    setAspspContent(paymentMethod) {
      const data = useAspspStore().aspspPaymentInformations[paymentMethod];
      useAspspStore().updatePrefillData(data.content);
      useAspspStore().updateEmail(data.content.EMAIL);
      useAspspStore().updatAspspFullAdress(
        this.formatFullAddress(data.content),
      );
    },
    updateShippingAndBillingInformations() {
      const isSameAddress = this.order.sameAsBilling;
      this.billingInformations = {
        type: "billing",
        firstName: this.order.billingFirstName,
        lastName: this.order.billingLastName,
        address: this.order.billingStreet1,
        address2: this.order.billingStreet2,
        address3: this.order.billingStreet3,
        address4: this.order.billingStreet4,
        phone: this.order.billingPhoneNumber,
        zipCode: this.order.billingZipCode,
        city: this.order.billingCity,
        country: this.order.billingCountry,
        email: this.order.billingEmail,
      };

      this.shippingInformations = {
        type: "shipping",
        firstName: isSameAddress
          ? this.order.billingFirstName
          : this.order.shippingFirstName,
        lastName: isSameAddress
          ? this.order.billingLastName
          : this.order.shippingLastName,
        address: isSameAddress
          ? this.order.billingStreet1
          : this.order.shippingStreet1,
        address2: isSameAddress
          ? this.order.billingStreet2
          : this.order.shippingStreet2,
        address3: isSameAddress
          ? this.order.billingStreet3
          : this.order.shippingStreet3,
        address4: isSameAddress
          ? this.order.billingStreet4
          : this.order.shippingStreet4,
        phone: isSameAddress
          ? this.order.billingPhoneNumber
          : this.order.shippingPhoneNumber,
        zipCode: isSameAddress
          ? this.order.billingZipCode
          : this.order.shippingZipCode,
        city: isSameAddress ? this.order.billingCity : this.order.shippingCity,
        country: isSameAddress
          ? this.order.billingCountry
          : this.order.shippingCountry,
      };
    },
  },
};
</script>

<style scoped>
/* Order Page UX: cross-fade iframe during reload (~200ms). */
.vanguard-payment-iframe {
  transition: opacity 200ms ease-in-out;
  opacity: 1;
}
.vanguard-payment-iframe.is-reloading {
  opacity: 0;
}

/* FORGE-412 — Placeholder affiche a la place du formulaire de paiement
 * quand le total du panier est a 0 (cf. `hasPayableTotal` computed).
 *
 * Style aligne sur les conventions FORGE-412 (radius / padding coherents
 * avec les autres cartes). Couleurs "info" (bleu pale) plutot que les
 * tokens brand `--vg-*` : on signale un etat informatif (pas un
 * succes / warning / erreur) qui doit rester neutre meme quand la
 * brand utilise un accent rouge ou orange.
 *
 * Reactive a un override marketing : les variables `--vg-info-*` sont
 * documentees comme tokens d'etat (cf. orderpage.css) et peuvent etre
 * surchargees par la CSS marketing par order page si besoin de matcher
 * une charte specifique.
 */
.payment-form-empty {
  background: var(--vg-info-bg, #eef2ff);
  color: var(--vg-info-text, #3743a3);
  border: 1px solid var(--vg-info-border, #c7d2fe);
  border-radius: var(--vg-card-radius, 12px);
  padding: 16px 20px;
  font-size: 14px;
  line-height: 1.45;
  text-align: center;
}
</style>
